/**
 * Centralized API error handling utilities
 */

export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  type: 'network' | 'auth' | 'server' | 'validation' | 'unknown';
}

/**
 * Parse API error and return standardized error object
 */
export function parseApiError(error: unknown): ApiError {
  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network error. Please check your internet connection and try again.',
      type: 'network',
    };
  }

  // Handle AbortError (request cancelled)
  if (error instanceof Error && error.name === 'AbortError') {
    return {
      message: 'Request was cancelled.',
      type: 'network',
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message;

    // Extract status code if present in the message
    const statusMatch = message.match(/status (\d{3})/);
    const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : undefined;

    // Firebase Auth errors
    if (message.includes('auth/')) {
      return {
        message: parseFirebaseAuthError(message),
        code: extractErrorCode(message),
        type: 'auth',
      };
    }

    // Specific status code handling
    if (statusCode === 404) {
      return {
        message: 'Resource not found.',
        statusCode: 404,
        type: 'unknown',
      };
    }

    // API validation errors
    if (message.includes('validation') || message.includes('required') || message.includes('invalid')) {
      return {
        message,
        statusCode,
        type: 'validation',
      };
    }

    // Server errors
    if (message.includes('server') || message.includes('internal') || statusCode === 500 || message.includes('500')) {
      return {
        message: 'Server error. Please try again later.',
        statusCode: 500,
        type: 'server',
      };
    }

    // Generic error
    return {
      message,
      statusCode,
      type: 'unknown',
    };
  }

  // Handle unknown error types
  return {
    message: 'An unexpected error occurred. Please try again.',
    type: 'unknown',
  };
}

/**
 * Parse Firebase Auth error messages into user-friendly messages
 */
function parseFirebaseAuthError(message: string): string {
  if (message.includes('auth/user-not-found')) {
    return 'No account found with this email address.';
  }
  if (message.includes('auth/wrong-password')) {
    return 'Incorrect password. Please try again.';
  }
  if (message.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists.';
  }
  if (message.includes('auth/weak-password')) {
    return 'Password is too weak. Please choose a stronger password.';
  }
  if (message.includes('auth/invalid-email')) {
    return 'Please enter a valid email address.';
  }
  if (message.includes('auth/user-disabled')) {
    return 'This account has been disabled. Please contact support.';
  }
  if (message.includes('auth/too-many-requests')) {
    return 'Too many failed attempts. Please try again later.';
  }
  if (message.includes('auth/network-request-failed')) {
    return 'Network error. Please check your connection and try again.';
  }
  if (message.includes('auth/popup-closed-by-user')) {
    return 'Sign-in was cancelled. Please try again.';
  }
  if (message.includes('auth/account-exists-with-different-credential')) {
    return 'An account already exists with the same email but different sign-in method.';
  }
  if (message.includes('auth/invalid-action-code')) {
    return 'Invalid or expired link. Please request a new one.';
  }

  return message;
}

/**
 * Extract error code from error message
 */
function extractErrorCode(message: string): string | undefined {
  const match = message.match(/auth\/[a-z-]+/);
  return match ? match[0] : undefined;
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: ApiError): boolean {
  return error.type === 'network' || error.type === 'server';
}

/**
 * Get retry delay based on error type
 */
export function getRetryDelay(error: ApiError, attempt: number): number {
  if (error.type === 'network') {
    return Math.min(1000 * Math.pow(2, attempt), 10000); // Exponential backoff, max 10s
  }
  if (error.type === 'server') {
    return Math.min(2000 * attempt, 30000); // Linear backoff, max 30s
  }
  return 0;
}

/**
 * Format error for display
 */
export function formatErrorForDisplay(error: ApiError): string {
  return error.message;
}

/**
 * Log error for debugging
 */
export function logApiError(error: unknown, context?: string): void {
  const parsedError = parseApiError(error);

  // Format details for the primary message to avoid "{}" display issues in some consoles
  const details = `[${parsedError.type.toUpperCase()}] ${parsedError.message}${parsedError.code ? ` (${parsedError.code})` : ''}${parsedError.statusCode ? ` [Status: ${parsedError.statusCode}]` : ''}`;
  const header = `API Error${context ? ` in ${context}` : ''}:`;

  // Suppress common expected 404s to reduce console noise
  if (parsedError.statusCode === 404 && context?.includes('/users/')) {
    console.debug(`[Info] ${header} User not yet created (Expected for new users)`);
    return;
  }

  // Downgrade 403 on community endpoints (access denied for private communities) to debug
  if (parsedError.statusCode === 403 && context?.includes('/communities/')) {
    console.debug(`[Info] ${header} Access denied (expected for private communities when not a member)`);
    return;
  }

  // @-mention search scoped to a community 403s if the requester isn't an
  // active member of it (see UserService.searchUsers) — an expected access
  // check, not a bug.
  if (parsedError.statusCode === 403 && context?.includes('/users/search')) {
    console.debug(`[Info] ${header} Community member search denied (not a member of the scoped community)`);
    return;
  }

  // Duplicate community name on create/rename is user-facing form validation
  // (the UI shows it inline via submitError), not a bug — downgrade so it
  // doesn't trigger React Native's full-screen "Console Error" overlay.
  if (parsedError.statusCode === 409 && context?.includes('/communities')) {
    console.debug(`[Info] ${header} Duplicate community name (expected form validation) — ${details}`);
    return;
  }

  // Already-resolved join requests surface as 400; UI treats as stale (expected noise)
  const isJoinRequestMutate =
    !!context && /\/join-requests\/[^/]+\/(approve|deny)\b/.test(context);
  if (parsedError.statusCode === 400 && isJoinRequestMutate) {
    console.debug(
      `[Info] ${header} Join request not pending or invalid (often stale notifications) — ${details}`
    );
    return;
  }

  const isInvitationRespond =
    !!context && /\/communities\/[^/]+\/invitations\/[^/]+\/respond\b/.test(context);
  if (parsedError.statusCode === 400 && isInvitationRespond) {
    console.debug(
      `[Info] ${header} Invitation respond rejected (expired, wrong id, or already processed) — ${details}`
    );
    return;
  }

  if (parsedError.statusCode === 404 && isInvitationRespond) {
    console.debug(
      `[Info] ${header} Invitation missing (withdrawn, expired, or stale notification) — ${details}`
    );
    return;
  }

  console.error(`${header} ${details}`, {
    originalError: error,
    type: parsedError.type,
    code: parsedError.code,
    status: parsedError.statusCode
  });
}
